import ShopDTO from "@/dto/shop/ShopDTO";
import OrderTab from "@/scenes/OrderManagement/enum/OrderTab";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import React, { useEffect, useRef } from "react";
import { Button, notification, Tooltip } from "antd";
import { useHistory } from "react-router";
import { AbnormalCount, batchGetAbnormalCount, OrderListCount } from "@/services/api/order";
import { QuestionCircleFilled } from "@ant-design/icons";
import { useGlobalStore } from "@/store/hooks";
import { when } from "mobx";
import { OrderSyncPhase } from "@/scenes/OrderManagement/store/OrderSyncStore";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

/**
 * 店铺异常订单生成时间 Mapping
 * 用于对比两个时间是否一致从而判断是否显示异常通知
 */
const shopAbnormalOrderRecordCreatedTimeMapping = new Map<number, number>();

/**
 * 此 Hook 用于进入店铺即加载订单数量，如果有异常订单，进一步提示。
 */
const useLoadOrderListCountOnMount = (shop: ShopDTO, tab: OrderTab) => {
    const orderManagementStore = useOrderManagementStore();
    const history = useHistory();
    /** 切换路由关闭通知的回调 */
    const closeNotificationRef = useRef<() => void>();
    const globalStore = useGlobalStore();

    useEffect(() => {
        /** 获取当前店铺的异常订单数量 */
        async function getShopAbnormalCount(orderListCount: OrderListCount) {
            if (!orderListCount || tab === OrderTab.ABNORMAL) return;

            const abnormalCountList = await batchGetAbnormalCount([shop.id], { hideModal: true });
            const shopAbnormalCount = abnormalCountList?.[0];
            if (!shopAbnormalCount) return;

            /** 加载出来的 orderListCount 与当前上下文的店铺不一致 */
            if (orderListCount.shopId !== orderManagementStore.shopId) return;

            if (!orderListCount.hasAbnormalOrders) return;
            if (!orderListCount.lastAbnormalOrderRecordCreatedTime) return;

            /** 生成异常时间无变化，证明在切换路由这段时候，没有产生新的异常，无需提示 */
            if (
                orderListCount.lastAbnormalOrderRecordCreatedTime ===
                shopAbnormalOrderRecordCreatedTimeMapping.get(shop.id)
            )
                return;

            return shopAbnormalCount;
        }

        async function loadOrderListCount() {
            await globalStore.ensureShopSetting([shop.id]);

            const shopSetting = globalStore.shopSettingMapping.get(shop.id);

            /** Admin进入发货易不允许同步订单的操作 */
            if (shopSetting.enableAutoSyncOrder && !globalStore.fromAdmin) {
                /** 等订单同步完成之后 */
                await when(() => orderManagementStore.orderSyncStore.orderSyncState?.phase === OrderSyncPhase.ENDED);
            }

            const orderListCount = await orderManagementStore.getOrderListCount(shop.id, tab);

            const shopAdvancedSetting = await globalStore.shopAdvancedSettingStore.ensureAdvancedSetting(shop.id);

            /** 不展示异常弹窗 */
            if (shopAdvancedSetting.enabledAbnormalOrderNotification === false) return;

            /** 有异常数量，打开通知 */
            const shopAbnormalCount = await getShopAbnormalCount(orderListCount);
            if (!shopAbnormalCount) return;

            showAbnormalOrderNotification(orderListCount, shopAbnormalCount);
        }

        /** 展示异常订单通知 */
        function showAbnormalOrderNotification(orderListCount: OrderListCount, shopAbnormalCount: AbnormalCount) {
            shopAbnormalOrderRecordCreatedTimeMapping.set(shop.id, orderListCount.lastAbnormalOrderRecordCreatedTime);

            notification.warning({
                key: shop.id.toString(),
                duration: 0,
                title: <span className="fb">异常订单提醒</span>,
                description: (
                    <div className="mt10">
                        <p>
                            <span>店铺:</span>
                            <span className="ml5">{shop.name}</span>
                        </p>
                        <p className="mt5">
                            <span>
                                异常平台订单
                                <Tooltip
                                    title="统计有异常信息的平台订单数量，异常订单列表数量是合并拆分后的订单数量"
                                    placement="bottom"
                                >
                                    <QuestionCircleFilled className="ml3" />
                                </Tooltip>
                                :
                            </span>
                            <span className="ml5 error fb">{shopAbnormalCount.totalCount}单</span>
                        </p>
                        <p className="tr mt10">
                            <Button
                                size="small"
                                onClick={() => {
                                    closeNotificationRef.current();
                                }}
                            >
                                关闭
                            </Button>
                            <Button
                                type="primary"
                                className="ml5"
                                size="small"
                                onClick={() => {
                                    history.push(`/order/list?shopId=${shop.id}&tab=ABNORMAL`);

                                    closeNotificationRef.current();

                                    collectEvent(DataTrackEvent.CLICK_ABNORMAL_NOTIFICATION, {
                                        shop_id: shop.id,
                                        shop_name: shop.name,
                                        shop_type: ShopTypeNameMap[shop.type],
                                        order_count: shopAbnormalCount.totalCount,
                                    });
                                }}
                            >
                                立即查看
                            </Button>
                        </p>
                    </div>
                ),
            });

            closeNotificationRef.current = () => {
                notification.destroy(shop.id.toString());

                closeNotificationRef.current = null;
            };
        }

        loadOrderListCount();

        return () => {
            closeNotificationRef.current?.();
        };
    }, [shop.id]);

    /** 手动点击异常订单Tab时需要关闭通知 */
    useEffect(() => {
        closeNotificationRef.current?.();
    }, [tab]);
};

export default useLoadOrderListCountOnMount;
